import { type GQLExhibitionLabel } from './graphql-object';
import { type SanityDocument } from '@sanity/types';
import { type PortableTextBlock } from '@portabletext/types';

/**
@remarks This is the info shown in the Object Modal.
*/
export interface IArtworkMinimum {
	accessionNumber: string;
	allArtistsFormatted?: string[];
	/**
	 * @remarks Detailed artist information with prefix, role, nationality, etc.
	 * When available, this should be preferred over allArtistsFormatted for richer display.
	 */
	artists?: IObjectPageArtist[];
	contextualLabel?: SanityDocument & {
		label: Array<PortableTextBlock>;
	};
	creditLine?: string;
	culture: string;
	date: string;
	dimensions?: string;
	exhibitionLabels?: GQLExhibitionLabel[];
	extendedStatuses?: string[];
	image: string;
	imageAlt?: string;
	imageSrcSet?: string;
	medium?: string;
	objectID: number;
	period?: string;
	primaryArtist?: string;
	primaryImageRestricted?: boolean;
	rightsAndReproduction?: string;
	title: string;
	url: string;
	webLabel?: string;
}

/**
@remarks This is a representation of the object wrapper for the object picker.
*/
export interface IObjectWrapper {
	_key: string;
	_type: string;
	objectId: number;
	objectThumbnailUrl: string;
}

interface IFacet {
	count: number;
	name: string;
	url: string;
}

export interface IObjectPageArtist {
	constituentID?: number;
	displayName: string;
	displayOrder?: number;
	gender?: string;
	nationality: string;
	prefix: string;
	role: string;
	roleID: number;
	roleTypeID?: number;
	ulanURL?: string;
	wikidataURL?: string;
}

/**
 * Constituent role IDs whose role label is hidden in artist display surfaces.
 * 19 = "Artist", 148 = "Maker" - the default expected role; showing it next to
 * an artist's name would be redundant. Origin: DIGCOLL-5741, DIGAPPDEV-4068.
 */
export const HIDDEN_ARTIST_ROLE_IDS: readonly number[] = [19, 148];

/**
 * Artist-count cutoff used by every artist-display surface in the app - promo
 * cards (collection-search results, exhibition object lists, related-artwork
 * shelves, welcome cards), the object detail page basics section, and the
 * clickable artist links section on the detail page.
 *
 * `> ARTIST_DISPLAY_THRESHOLD` collapses the artist list to a
 * "Multiple artists/makers" label; `<= ARTIST_DISPLAY_THRESHOLD` lists each one.
 *
 * Matches the UX-aligned rule stated in DIGAPPDEV-2568's acceptance criteria:
 * 1-3 artists -> list each one, 4+ -> collapse to a link to the tombstone section.
 */
export const ARTIST_DISPLAY_THRESHOLD = 3 as const;

export interface IObjectPageData {
	artists: IObjectPageArtist[];
	collectionIDs: number[];
	collectionObject: {
		accessionNumber: string;
		accessionYear: string;
		crdDeptID: number;
		culture?: string; // "Populated for about 40% of the collection"
		deaccession: any;
		deaccessionInfo: any;
		exhibitionLabelOverrides: unknown[];
		facetInfo: {
			artistMaker: IFacet[] | null;
			dateEra: IFacet[] | null;
			departmentIDs: IFacet[] | null;
			geographicLocations: IFacet[] | null;
			methodMaterials: IFacet[] | null;
		};
		informationBoxes?: unknown[]; //TODO figure out type
		isTimelineWork: boolean;
		loanObject: boolean;
		location: {
			gallery: string;
			galleryLocationURL: string;
			onViewMessage: string;
		};
		media: {
			audios: unknown[];
			images: {
				additionalImages: ICollectionImage[];
				primaryImage: ICollectionImage;
			};
		};
		metadata: {
			metaCanonicalURL: string;
			metaDescription: string;
			metaKeywords: string;
			metaTitle: string;
			pageTitle: string;
		};
		objectID: number;
		onlineResources: {
			toahEssays: {
				name: string;
				url: string;
			}[];
			toahTimelines: {
				name: string;
				url: string;
			}[];
		};
		pagingInfo: {
			backToSearchText: string;
			backToSearchURL: string;
			currentItemIndex: number;
			nextImageTitle: string;
			nextImageURL: string;
			nextItemURL: string;
			nextPosition: string;
			previousImageTitle: string;
			previousImageURL: string;
			previousItemURL: string;
			previousPosition: string;
			totalItemCount: number;
		};
		relatedObjects: {
			accessionNumber: string;
			artist: string;
			artworkURL: string;
			date: string;
			galleryLink: string;
			imageURL: string;
			location: string;
			medium: string;
			relatedGalleryLink: string;
			teaserText: string;
			title: string;
		}[];
		// Dual-shape during the DIGAPPDEV-4642 transition: the legacy Collection
		// API returns hydrated card objects; the id-only Collection API
		// (DIGAPPDEV-4532/4534) returns bare crdId integers that met-js hydrates
		// from Elastic. Entries are branched on `typeof entry === 'number'`.
		// Collapse to number[] once the id-only API is live everywhere.
		relatedObjectsCurated?: (
			| number
			| {
					accessionNumber: string;
					artist: string;
					artworkURL: string;
					date: string;
					galleryLink: string;
					imageURL: string;
					location: string;
					medium: string;
					objectID: number;
					relatedGalleryLink: string;
					teaserText: string;
					title: string;
			  }
		)[];
		relatedPublications: unknown[]; //Doesnt matter - we will never use this again
		titles: { primaryTitle: string; secondaryTitles: string | null };
		tombstone: { label: string; text: string }[];
		translations: {
			informationBoxes: [];
			language: string;
			languageCode: string;
			media: {
				audios: unknown[];
				images: {
					additionalImages: ICollectionImage[];
					primaryImage: ICollectionImage;
				};
			};
			metadata: {
				metaCanonicalURL: string;
				metaDescription: string;
				metaKeywords: string;
				metaTitle: string;
				pageTitle: string;
			}[];
			title: string;
			tombstone: { label: string; text: string }[];
			webLabel: {
				label: string;
				text: string;
			};
		}[];
		webLabel: {
			label: string;
			text: string;
		};
		wikidataURL: string;
	};
	crdObjectId: number;
	culture: string;
	dated: string;
	departmentAcronym: string;
	departmentID: number;
	extendedStatuses: string[];
	gallery: string;
	isOnView: boolean;
	objectStatusID: number;
	period: string;
	primaryArtist: string;
	translations: {
		fieldID: number;
		label: string;
		languageCode: string;
		text: string;
	}[];
}

export interface ICollectionImage {
	altText: string;
	caption: string;
	imageNo: number;
	imageUrl: string;
	isOasc: boolean;
	isRightToLeft: boolean;
	isThumbnail: boolean;
	isZoomable: boolean;
	linkUrl: string;
	objectUrl: string;
	originalImageUrl: string;
	publicCaption: string;
	title?: string;
	webImageHeight: number;
	webImageUrl: string;
	webImageWidth: number;
}

export interface IArchivalLabelRawObject {
	__typename: 'Object';
	images: {
		__typename: 'Images';
		all: IArchivalLabelImage[];
	};
	objectID: number;
}

export interface IArchivalLabelImage {
	__typename: 'Image';
	altText: string;
	caption: string;
	mainImageUrl: string;
	mediaXRefID: number;
	publicCaption: string;
	sort: number;
	textEntries: {
		objectID: number;
		purpose: string;
		textEntry: string;
	}[];
	thumbnailImageUrl: string;
	title: string;
}

export interface IObjectAsLauderArchivalLabel {
	archivalLabelID: number;
	image: string; // image URL
	imageAlt: string;
	images: IArchivalLabelImage[];
	objectID: number;
	/**
	 * @example "Collectors"
	 */
	purpose: string;
	title: string;
	/**
	 * @example "<p>A small gold-colored plaque affixed to the back of Léger's&nbsp;<i>Composition (The Typographer)</i><i>&nbsp;</i>(1918–19) identifies Mr. and Mrs. Wallace K. Harrison as the owners of the painting. (A detail of the work's back appears at left.) The label was probably produced for the major Léger exhibition of 1953–54 that originated at the Art Institute of Chicago and traveled to the San Francisco Museum of Art and the Museum of Modern Art, New York. The Harrisons loaned the painting to MoMA again the following summer (1955) for a display of works from private collections. Harrison (1895–1981), a successful architect who helped design and build New York City's Rockefeller Center (1929–39), served as a trustee of MoMA from 1939 until 1968.</p>\r\n" +
//     '\r\n' +
//     "<p>During the war years, from 1942 to 1945, Léger spent an extended period at the Harrisons' home in West Hills, Long Island. He painted&nbsp;<i>The Divers</i>, a 33-foot-long mural comprised of six canvases, for their curved living room wall (1942; Museum Ludwig, Cologne) and created a composition for the bottom of their swimming pool. In 1974, the Harrisons sold both their West Hills house and Léger's&nbsp;<i>Composition (The Typographer)</i>&nbsp;to their friend, the art collector and dealer Harold Diamond.</p>\r\n" +
//     '\r\n' +
//     '<p>\r\n' +
//     '<b>For more information, see</b>:</p>\r\n' +
//     '<p>Jozefacka, Anna, "Harrison, Wallace Kirkman",&nbsp;<i>The Modern Art Index Project</i>&nbsp;(July 2017), Leonard A. Lauder Research Center for Modern Art, The Metropolitan Museum of Art.&nbsp;<a href="https://doi.org/10.57011/JIQW2016">https://doi.org/10.57011/JIQW2016</a></p>\r\n' +
//     '\r\n' +
//     '<p>Newhouse, Victoria.&nbsp;<i>Wallace K. Harrison, Architect.</i><i>&nbsp;</i>New York: Rizzoli, 1989.</p>'
	 */
	webLabel: string;
}
